import { action, computed, makeObservable, observable, when, IReactionDisposer } from "mobx";
import { useEffect } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { SettingsTabBarSectionContent } from "@/pages/settings/components/tab-bar/settings-tab-bar.types";
import { MDSIconKind } from "@/mds";
import { enumModule } from "@/modules/enum";
import {
  AGENT_ACTIVITY_ID_SEARCH_PARAM,
  AGENT_PROJECT_ID_SEARCH_PARAM,
  AGENT_SEARCH_PARAM,
  AGENT_TASK_ID_SEARCH_PARAM,
} from "@/store/agent-overlay/types";

export class SettingsModalStore extends AppSubStore {
  /**
   * The validated tab id from the `?settings=` query param, or null when the param
   * is absent. Fed from `useSearchParams()` via `useSyncSettingsSearchParam()`.
   * This is the source of truth — `isOpen` and `activeTab` are derived from it.
   */
  settingsParam: SettingsTabId | null = null;

  pendingOpenAfterPermissions: SettingsTabId | null = null;

  private pendingPermissionsDisposer: IReactionDisposer | null = null;
  private setSearchParams: SetURLSearchParams | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      SettingsModalStore,
      | "pendingPermissionsDisposer"
      | "isDebugTab"
      | "isMemAgentTab"
      | "isTabAvailable"
      | "setSearchParams"
      | "updateSettingsParam"
    >(this, {
      settingsParam: observable,
      isOpen: computed,
      activeTab: computed,
      pendingOpenAfterPermissions: observable,
      pendingPermissionsDisposer: false,
      setSearchParams: observable,
      tabBarSections: computed,
      open: action,
      close: action,
      navigateToTab: action,
      queueOpenAfterPermissions: action,
      consumePendingOpen: action,
      setSettingsParam: action,
      isDebugTab: false,
      isMemAgentTab: false,
      isTabAvailable: false,
      updateSettingsParam: false,
      useSyncSettingsSearchParam: action,
    });
  }

  get isOpen(): boolean {
    return this.settingsParam !== null;
  }

  get activeTab(): SettingsTabId {
    return this.settingsParam ?? SettingsTabId.NotificationPreferencesPage;
  }

  get tabBarSections(): SettingsTabBarSectionContent[] {
    const makeItem = (id: SettingsTabId, label: string, icon?: MDSIconKind) => ({
      id,
      label,
      icon,
      onClick: () => this.navigateToTab(id),
    });

    const sections: SettingsTabBarSectionContent[] = [
      {
        id: "general-section",
        label: "General",
        items: [
          makeItem(SettingsTabId.NotificationPreferencesPage, "Notifications", MDSIconKind.Bell),
          makeItem(SettingsTabId.VoiceModePage, "Voice mode", MDSIconKind.Microphone),
          makeItem(SettingsTabId.ChatPage, "Chat", MDSIconKind.Message),
          makeItem(SettingsTabId.TemplatesPage, "Templates", MDSIconKind.AlignLeft),
          makeItem(SettingsTabId.AppearancePage, "Appearance", MDSIconKind.Palette),
        ],
      },
      {
        id: "apps-and-integrations-section",
        label: "Apps and integrations",
        items: [
          makeItem(
            SettingsTabId.CalendarAndMeetingsPage,
            "Calendar and meetings",
            MDSIconKind.Calendar
          ),
          makeItem(SettingsTabId.ChromeExtensionPage, "Chrome extension", MDSIconKind.Chrome),
          makeItem(SettingsTabId.EmailToMemPage, "Email to Mem", MDSIconKind.Envelope),
          makeItem(SettingsTabId.McpPage, "MCP", MDSIconKind.Mcp),
          ...((this.store.spaceAccountFeatureFlags.getFlagStatus("mem-agent") ?? false)
            ? [makeItem(SettingsTabId.MemAgentPage, "Mem Agent", MDSIconKind.Robot)]
            : []),
          makeItem(SettingsTabId.ZapierPage, "Zapier", MDSIconKind.BoltOutline),
        ],
      },
      {
        id: "developer-section",
        label: "Developer",
        items: [makeItem(SettingsTabId.ApiKeysPage, "API Keys", MDSIconKind.Code)],
      },
      {
        id: "account-section",
        label: "Account",
        items: [
          makeItem(SettingsTabId.ProfilePage, "Profile", MDSIconKind.User),
          makeItem(SettingsTabId.ImportsPage, "My imports", MDSIconKind.Import),
          makeItem(
            SettingsTabId.SubscriptionManagementPage,
            "Subscription",
            MDSIconKind.CreditCard
          ),
          makeItem(SettingsTabId.ExportsPage, "Exports", MDSIconKind.ArrowUpFromLine),
          makeItem(SettingsTabId.ReferralProgramPage, "Referral program", MDSIconKind.Share),
        ],
      },
    ];

    if (this.store.debug.debugModeEnabled) {
      sections.push({
        id: "debug-section",
        label: "Debug",
        items: [
          makeItem(SettingsTabId.DebugAccountPage, "Debug account", MDSIconKind.GearCode),
          makeItem(SettingsTabId.DebugSandboxPage, "Debug sandbox", MDSIconKind.GearCode),
        ],
      });
    }

    return sections;
  }

  private isDebugTab(tabId: SettingsTabId): boolean {
    return tabId === SettingsTabId.DebugAccountPage || tabId === SettingsTabId.DebugSandboxPage;
  }

  private isMemAgentTab(tabId: SettingsTabId): boolean {
    return tabId === SettingsTabId.MemAgentPage;
  }

  private isTabAvailable(tabId: SettingsTabId): boolean {
    if (this.isDebugTab(tabId) && !this.store.debug.debugModeEnabled) {
      return false;
    }

    if (
      this.isMemAgentTab(tabId) &&
      !(this.store.spaceAccountFeatureFlags.getFlagStatus("mem-agent") ?? false)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Writes the `?settings=` query param to the URL. Pass null to remove it.
   * Uses `replace` to avoid polluting browser history with tab switches.
   */
  private updateSettingsParam(tabId: SettingsTabId | null) {
    if (!this.setSearchParams) return;

    this.setSearchParams(
      prevParams => {
        if (tabId) {
          prevParams.delete(AGENT_SEARCH_PARAM);
          prevParams.delete(AGENT_PROJECT_ID_SEARCH_PARAM);
          prevParams.delete(AGENT_TASK_ID_SEARCH_PARAM);
          prevParams.delete(AGENT_ACTIVITY_ID_SEARCH_PARAM);
          prevParams.set("settings", tabId);
        } else {
          prevParams.delete("settings");
        }

        return prevParams;
      },
      { replace: true }
    );
  }

  open(tabId?: SettingsTabId) {
    const resolvedTab = tabId ?? SettingsTabId.NotificationPreferencesPage;
    if (!this.isTabAvailable(resolvedTab)) return;

    this.updateSettingsParam(resolvedTab);
  }

  close() {
    this.updateSettingsParam(null);
  }

  navigateToTab(tabId: SettingsTabId) {
    if (!this.isTabAvailable(tabId)) return;

    this.updateSettingsParam(tabId);
  }

  queueOpenAfterPermissions(tabId: SettingsTabId) {
    this.pendingOpenAfterPermissions = tabId;

    // Dispose any existing watcher
    this.pendingPermissionsDisposer?.();

    // Set up a one-shot watcher: when the system permissions modal closes,
    // open the settings modal on the queued tab.
    this.pendingPermissionsDisposer = when(
      () =>
        this.pendingOpenAfterPermissions !== null &&
        !this.store.systemPermissions.ui.isAnyModalOpen,
      () => {
        this.consumePendingOpen();
        this.pendingPermissionsDisposer = null;
      }
    );
  }

  consumePendingOpen() {
    if (this.pendingOpenAfterPermissions === null) return;

    const tabId = this.pendingOpenAfterPermissions;
    this.pendingOpenAfterPermissions = null;
    this.pendingPermissionsDisposer?.();
    this.pendingPermissionsDisposer = null;
    this.open(tabId);
  }

  /**
   * Sets the validated settings param from the URL. Called by the hook below.
   */
  setSettingsParam(tabId: SettingsTabId | null) {
    this.settingsParam = tabId;
  }

  /**
   * Syncs the `?settings=<tab-id>` query param into this store, following the
   * same pattern as `useSyncNavigation()` which captures `navigate`.
   *
   * The URL is the source of truth. `isOpen` and `activeTab` are computed from
   * `settingsParam`, which is fed from `useSearchParams()`. Actions like `open()`
   * and `close()` write to the URL via the captured `setSearchParams`, which
   * triggers a re-render, which feeds the new value back into the store.
   *
   * Called from `AppStore.useInitializeAppStoreEffects()`.
   */
  useSyncSettingsSearchParam() {
    const [searchParams, setSearchParams] = useSearchParams();
    this.setSearchParams = setSearchParams;

    const rawSettingsParam = searchParams.get("settings");
    const rawAgentParam = searchParams.get(AGENT_SEARCH_PARAM);
    const rawProjectId = searchParams.get(AGENT_PROJECT_ID_SEARCH_PARAM);
    const rawTaskId = searchParams.get(AGENT_TASK_ID_SEARCH_PARAM);
    const rawActivityId = searchParams.get(AGENT_ACTIVITY_ID_SEARCH_PARAM);
    const hasAgentParams = !!(rawAgentParam || rawProjectId || rawTaskId || rawActivityId);

    useEffect(() => {
      if (rawSettingsParam) {
        const validTab = enumModule.values(SettingsTabId).find(v => v === rawSettingsParam);

        if (validTab && this.isTabAvailable(validTab)) {
          this.setSettingsParam(validTab);

          if (hasAgentParams) {
            this.updateSettingsParam(validTab);
          }
        } else {
          this.setSettingsParam(null);
          this.updateSettingsParam(null);
        }
      } else {
        this.setSettingsParam(null);
      }
    }, [rawSettingsParam, hasAgentParams]);
  }
}
